/**
 * Configurator flow registry + resolver.
 *
 * The pre-payment configurator has TWO flows in code. Each flow is a
 * self-contained wizard with its own base path:
 *   - `v0` — the short flow (Theme → Colors & Art Style → Payment review → Payment), the default.
 *   - `v3` — the full flow (Design → Features → Players → Finish). PARKED: its code stays in the
 *     repo, but every owner is funnelled through `v0`. Nothing resolves to it (see
 *     `PARKED_FLOW_REMAP`) and `/setup` only redirects to `/easy-setup` (`ParkedSetupRedirect` in
 *     `src/routes.tsx`).
 *
 * (The Mexican-market `-mx` variants are no longer routable: their base paths now
 * redirect to the base flow via `MxHandoffRedirect` in `src/routes.tsx`, which mints
 * the session in MXN off the durable `startProperties` hand-off keys instead.)
 *
 * Which flow a new owner lands on is chosen from the external marketing site's
 * `startProperties` hand-off (`?startProperties=<base64-JSON>`), by an optional
 * `flow` field. Anything absent or unknown falls back to the default (`v0`), and a
 * parked flow is remapped to its replacement, so a broken/spoofed/stale hand-off never strands the owner.
 *
 * This module is the SINGLE source of truth for "which flow, and where does it
 * live". It is deliberately app-level (not inside either flow folder) so the
 * shared entry points — the root redirect, login, register, and the resume
 * banners — can all agree without importing a specific flow's internals.
 */

/** A single configurator flow: its id and where it is mounted. */
export interface ConfiguratorFlow {
  /** Stable id, matched against `startProperties.flow`. */
  id: string;
  /** Full-bleed base path the flow's router is mounted under. */
  base: string;
  /** Deep link to the flow's payment step (used by the "Retry payment" banner). */
  paymentPath: string;
  /**
   * ISO code the flow's sessions are MINTED in (`POST /session/load { currency }`), which
   * fixes the currency every price in that flow is quoted in.
   *
   * It has to be decided at mint time: `POST /session/set-currency` only accepts currencies
   * flagged `is_active` in the catalog, so a session minted in an INACTIVE currency cannot be
   * switched afterwards. (Active-currency sessions CAN be re-priced via set-currency — e.g. the
   * configurator's region picker re-prices between the active USD / MXN markets.)
   */
  currency: string;
}

/** Registry of all configurator flows. The FIRST entry is the default. */
export const CONFIGURATOR_FLOWS: readonly ConfiguratorFlow[] = [
  { id: 'v0', base: '/easy-setup', paymentPath: '/easy-setup/payment', currency: 'USD' },
  { id: 'v3', base: '/setup', paymentPath: '/setup/finish', currency: 'USD' },
];

/** Default flow id: the short flow every owner is funnelled through. */
export const DEFAULT_FLOW_ID = 'v0';

/** Query-param name carrying the base64-encoded hand-off object (mirrors startProperties). */
const START_PROPERTIES_PARAM = 'startProperties';
/** sessionStorage key the boot shim stashes the RAW `startProperties` param under. */
const START_PROPERTIES_STASH_KEY = 'ows-start-properties';
/**
 * sessionStorage key the boot shim (`src/dev.js`) stashes the pre-extracted `flow` under.
 * sessionStorage (not localStorage) is deliberate: the hand-off choice lasts for the tab
 * session — so a mid-flow reload / auth-gate redirect / in-session resume keeps the same
 * flow — but a genuinely new brand later (no hand-off) falls back to the default flow,
 * rather than a stale choice sticking to the browser forever.
 */
export const START_FLOW_KEY = 'ows-start-flow';

/** The default flow (first registry entry). */
export const defaultConfiguratorFlow = (): ConfiguratorFlow => CONFIGURATOR_FLOWS[0];

/** Look up a flow by id, or `null` for an unknown/absent id. */
export const configuratorFlowById = (id: string | null | undefined): ConfiguratorFlow | null =>
  (id && CONFIGURATOR_FLOWS.find((f) => f.id === id)) || null;

/** Retired Mexican-market flow ids -> their surviving base flow. The `-mx` flows were consolidated
 *  away; a hand-off still carrying the old token must land on the base flow (short `v0` / full `v3`),
 *  not fall through to the default. This is the flow-token counterpart to `MxHandoffRedirect`, which
 *  handles the same remap on the URL-path axis. */
const RETIRED_FLOW_REMAP: Readonly<Record<string, string>> = { 'v0-mx': 'v0', 'v3-mx': 'v3' };

/**
 * Flows kept in code but no longer offered -> the flow every owner gets instead. Applied to every
 * resolution (path, hand-off, default), so nothing can route an owner into a parked flow.
 */
const PARKED_FLOW_REMAP: Readonly<Record<string, string>> = { v3: 'v0' };

const unpark = (flow: ConfiguratorFlow): ConfiguratorFlow => configuratorFlowById(PARKED_FLOW_REMAP[flow.id]) ?? flow;

/**
 * UTF-8-safe base64 decode tolerant of URL-safe base64 and missing padding. A `+`
 * in a query string is decoded to a space by `URLSearchParams`, and the sender may
 * use standard (`+` `/`) or URL-safe (`-` `_`) base64. Mirrors the decode in the
 * configurator's `lib/startProperties.ts` (kept local to avoid importing a flow's internals).
 */
function decodeBase64Utf8(raw: string): string {
  let b64 = raw.replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad) b64 += '='.repeat(4 - pad);
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Raw `startProperties` value from the URL, else the boot-time sessionStorage stash. */
function readRawStartProperties(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(START_PROPERTIES_PARAM);
    if (fromUrl) return fromUrl;
  } catch {
    /* URL unparseable — fall through to the stash. */
  }
  try {
    return sessionStorage.getItem(START_PROPERTIES_STASH_KEY);
  } catch {
    return null;
  }
}

/** Read the `flow` id out of the raw `startProperties` param, or `null`. */
function flowIdFromStartProperties(): string | null {
  const raw = readRawStartProperties();
  if (!raw) return null;
  try {
    const decoded: unknown = JSON.parse(decodeBase64Utf8(raw));
    if (!decoded || typeof decoded !== 'object') return null;
    const flow = (decoded as Record<string, unknown>).flow;
    return typeof flow === 'string' && flow.trim() ? flow.trim() : null;
  } catch {
    return null;
  }
}

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * The flow whose base path the browser is CURRENTLY standing on, else `null`.
 *
 * Matched as a whole path segment (`/setup` and `/setup/finish`, but never a path that
 * merely CONTAINS a base, e.g. `/account/setup`), so an unrelated route can't be mistaken
 * for a flow.
 */
function flowFromCurrentPath(): ConfiguratorFlow | null {
  let pathname: string;
  try {
    pathname = window.location.pathname;
  } catch {
    return null;
  }
  return CONFIGURATOR_FLOWS.find((f) => pathname === f.base || pathname.startsWith(`${f.base}/`)) ?? null;
}

/**
 * Resolve which configurator flow the owner belongs to, defaulting to `v0`. Whatever the signal
 * below picks, a parked flow (`v3`) is remapped to its replacement (`v0`), so this never returns one.
 *
 * Order (first hit wins):
 *  1. the CURRENT path, when it is already inside a flow's base. Standing on
 *     `/easy-setup` IS being in that flow, and this is the only signal that survives a
 *     direct link / bookmark with no hand-off. Never matches on `/`, so the root redirect
 *     (which runs before any flow is mounted) still falls through to the hand-off below.
 *  2. `sessionStorage['ows-start-flow']` — pre-extracted by the boot shim, so a `flow`
 *     hand-off survives the auth-gate `/login` redirect (which drops the query string) and
 *     the tab session (so a mid-flow reload / in-session resume keeps the same flow).
 *  3. the raw `startProperties` param (URL or its sessionStorage stash), decoded here — the
 *     first arrival, before the boot shim's stash exists.
 *  4. the default flow (`v0`) — no hand-off.
 */
export function resolveConfiguratorFlow(): ConfiguratorFlow {
  const fromPath = flowFromCurrentPath();
  if (fromPath) return unpark(fromPath);
  const fromHandoff = safeSessionGet(START_FLOW_KEY) || flowIdFromStartProperties();
  const normalized = (fromHandoff && RETIRED_FLOW_REMAP[fromHandoff]) || fromHandoff;
  return unpark(configuratorFlowById(normalized) ?? defaultConfiguratorFlow());
}

/** Convenience: the resolved flow's base path (e.g. `/easy-setup`). */
export const resolveConfiguratorFlowBase = (): string => resolveConfiguratorFlow().base;

export default resolveConfiguratorFlow;
